import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import colors from "@streeteasy/design-system/deprecated/colors";
import Modal from "@streeteasy/design-system/deprecated/Modal";
import { arrayOf, shape, string } from "prop-types";
import uuid from "uuid";

import ContactButtonBox from "src/shared/components/ContactButtonBox";
import ContactButtons from "src/shared/components/ContactButtons";
import Drawer from "src/shared/components/Drawer";
import { ModalContent } from "src/shared/components/Drawer/styled";
import StickyButtons from "src/shared/contact-boxes/components/StickyButtons";
import { useIsMobile } from "src/shared/utilities/customHooks";
import deviceFingerprint from "src/shared/utilities/deviceFingerprint";
import listingProps from "src/shared/utilities/graphql/listingProps";
import {
  useSticky,
  withStickyContextProvider
} from "src/shared/utilities/stickyHelpers";

import { gaEventForClick, gaEventForSend } from "../../helpers/ga";
import ConfirmationModal from "../ConfirmationModal";
import ContactForm from "../ContactForm";
import LeadType from "./LeadType";
import * as S from "./styled";

const RentalContactContainer = ({
  data: { rental, rental_contacts, viewer }
}) => {
  const isMobile = useIsMobile();
  const [formType, updateFormType] = useState(LeadType.request.type);
  const [contactFormOpen, updateContactFormOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useSticky(isMobile);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const defaultFormValues = {
    name: viewer.name || "",
    email: viewer.email || "",
    phone: viewer.phone || "",
    searchPartnersEmails: "",
    preferredTourDate: "",
    timeOfDay: "",
    tourType: "in_person",
    message: "",
    question: "",
    datetimes: [{ date: null, time: null, key: uuid.v4() }]
  };
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [formErrors, setFormErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const openContactForm = () => {
    updateContactFormOpen(true);
    if (stickyVisible) {
      setStickyVisible(false);
    }
  };

  const handleContentClose = () => {
    if (isConfirmationOpen) {
      setFormValues(defaultFormValues);
    }
    setConfirmationOpen(false);
    updateContactFormOpen(false);
  };

  const displayModalForm = leadType => {
    openContactForm();
    updateFormType(leadType);
    gaEventForClick(LeadType[leadType].ga_events.click, stickyVisible);
  };

  const ContactFormContent = LeadType[formType].component;

  const handleInputChange = (value, event) =>
    setFormValues({ ...formValues, [event.target.name]: value });

  const mutationQuery = LeadType[formType].mutation;
  const [sendLead] = useMutation(mutationQuery, {
    onCompleted: ({
      request_tour: requestTour = {},
      ask_question: askQuestion = {}
    }) => {
      if (requestTour.recipients || askQuestion.recipients) {
        setConfirmationOpen(true);
        gaEventForSend(LeadType[formType].ga_events.send);
      }
    }
  });

  const handleSendRequestClick = () => {
    const message_details = {
      sender_email: formValues.email,
      search_partners_emails: formValues.searchPartnersEmails.split(","),
      sender_phone: formValues.phone,
      sender_name: formValues.name,
      ...LeadType[formType].email_details(formValues, rental.address.unit)
    };

    const input = {
      message_owner_id: rental.id,
      message_owner_class: "rental",
      device_fingerprint: deviceFingerprint(),
      message_details
    };
    return sendLead({ variables: { input } });
  };

  const renderConfirmation = onClose => {
    const listedByName =
      rental_contacts[0] && rental_contacts[0].name
        ? rental_contacts[0].name
        : "Unknown";

    const { confirmationHeader, confirmationText } = LeadType[
      formType
    ].confirmation(listedByName);

    return (
      <ConfirmationModal
        confirmationHeader={confirmationHeader}
        confirmationText={confirmationText}
        formValues={formValues}
        handleContentClose={onClose}
        listedByName={listedByName}
        rentalContact={rental_contacts[0]}
      />
    );
  };

  const renderForm = (onClose, displayDetails) => (
    <ContactForm
      contacts={rental_contacts}
      displayDetails={displayDetails}
      formErrors={formErrors}
      formValues={formValues}
      isOwnerListing={rental.source === "Owner"}
      onChange={handleInputChange}
      onClose={onClose}
      onSendRequest={handleSendRequestClick}
      render={inputError => (
        <ContactFormContent
          formErrors={formErrors}
          formValues={formValues}
          inputError={inputError}
          onChange={handleInputChange}
          setFormErrors={setFormErrors}
          setSubmitting={setSubmitting}
        />
      )}
      setFormErrors={setFormErrors}
      setSubmitting={setSubmitting}
      submitting={submitting}
      userDetails={viewer}
    />
  );

  const renderContactContent = (onClose = handleContentClose) => {
    const { buttonCaption, subtitle, title } = LeadType[formType];
    const displayDetails = isMobile
      ? { title: null, subtitle: null, buttonCaption }
      : { title, subtitle, buttonCaption };

    return isConfirmationOpen
      ? renderConfirmation(onClose)
      : renderForm(onClose, displayDetails);
  };

  const renderButtons = isVertical => (
    <ContactButtons
      isVertical={isVertical}
      primaryClick={() => displayModalForm(LeadType.request.type)}
      primaryText={LeadType.request.title}
      secondaryClick={() => displayModalForm(LeadType.question.type)}
      secondaryText={LeadType.question.title}
    />
  );

  const renderPortal = () => (
    <StickyButtons isMobile={isMobile} listing={rental}>
      {renderButtons(false)}
    </StickyButtons>
  );

  return (
    <ContactButtonBox isMobile={isMobile}>
      {renderButtons(true)}
      {isMobile && (
        <>
          {contactFormOpen && (
            <S.MobileHeaderTitle>
              {LeadType[formType].title}
            </S.MobileHeaderTitle>
          )}
          <Modal
            closeButtonColor={colors.pureWhite}
            isOpen={contactFormOpen}
            onRequestClose={handleContentClose}
            theme={isConfirmationOpen ? "lightBox" : "classicBlueHeader"}
            top={isConfirmationOpen ? "20vh" : "0"}
          >
            <ModalContent>{renderContactContent()}</ModalContent>
          </Modal>
        </>
      )}
      {!isMobile && (
        <Drawer
          handleOnClose={handleContentClose}
          isOpen={contactFormOpen}
          render={handleDrawerClose => renderContactContent(handleDrawerClose)}
        />
      )}
      {renderPortal()}
    </ContactButtonBox>
  );
};

RentalContactContainer.propTypes = {
  data: shape({
    rental: shape(listingProps),
    viewer: shape({
      name: string,
      email: string,
      phone: string
    }),
    rental_contacts: arrayOf(
      shape({
        business_name: string,
        image: shape({ url: string }),
        industry_professional: shape({ headshot: shape({ url: string }) }),
        name: string,
        phone: string,
        account_url: string
      })
    )
  }).isRequired
};

export default withStickyContextProvider(RentalContactContainer);
